import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, Database, FolderOpen } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/500.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import CompanyStorageBrowser from '../../company-config/components/CompanyStorageBrowser';
import '../../process-config/ConfigStatusView2.css';
import './PainelControleStorageConfig.css';

const API_BASE_URL = getApiBaseUrl();
const ACCENT = '#4F46E5';

function formatGb(value, digits = 2) {
  const parsed = Number(value || 0);
  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatPctLabel(pct) {
  if (pct === 0) return '0%';
  if (pct < 1) {
    return `${pct.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;
  }
  return `${Math.round(pct)}%`;
}

function barColorForPct(pct) {
  if (pct >= 95) return '#DC2626';
  if (pct >= 80) return '#D97706';
  return ACCENT;
}

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

export default function PainelControleStorageConfig() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [storage, setStorage] = useState({
    usedGb: 0,
    maxGb: 50,
    percentUsed: 0,
  });
  const [browserOpen, setBrowserOpen] = useState(false);

  const loadStorage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/config/company/storage`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result.success && result.data) {
        setStorage({
          usedGb: Number(result.data.usedGb || 0),
          maxGb: Number(result.data.maxGb || 50),
          percentUsed: Number(result.data.percentUsed || 0),
        });
      } else {
        setError(result.message || 'Falha ao carregar armazenamento');
      }
    } catch (_) {
      setError('Erro ao carregar armazenamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStorage();
  }, [loadStorage]);

  const used = Number(storage.usedGb || 0);
  const total = Math.max(0.01, Number(storage.maxGb || 50));
  const percent = Math.min(100, Math.max(0, storage.percentUsed || (used / total) * 100));
  const free = Math.max(0, total - used);
  const barColor = useMemo(() => barColorForPct(percent), [percent]);
  const pctBar = Math.max(percent > 0 ? 2 : 0, percent);

  return (
    <div className="pcs2-page pc-storage-page">
      <Pcs2Section
        icon={Database}
        title="Utilização do espaço"
        desc="Visualize o consumo do armazenamento do seu ambiente em tempo real."
      >
        <div className="pc-storage-card" aria-label="Armazenamento">
          <div className="pc-storage-card-head">
            <div className="pc-storage-card-title-row">
              <span className="pc-storage-card-icon" style={{ color: ACCENT }} aria-hidden>
                <Database size={17} strokeWidth={1.8} />
              </span>
              <span className="pc-storage-card-title">Armazenamento</span>
            </div>
          </div>

          <div className="pc-storage-card-usage">
            <div className="pc-storage-card-values">
              {loading ? (
                <span className="pc-storage-card-used">Carregando…</span>
              ) : (
                <>
                  <span className="pc-storage-card-used">{formatGb(used, 2)} GB</span>
                  <span className="pc-storage-card-total">de {formatGb(total, 1)} GB</span>
                </>
              )}
            </div>

            <div
              className="pc-storage-card-track"
              role="progressbar"
              aria-label="Espaço utilizado"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={loading ? 0 : Math.round(percent)}
            >
              <div
                className="pc-storage-card-fill"
                style={{
                  width: loading ? '0%' : `${pctBar}%`,
                  background: barColor,
                }}
              />
            </div>

            <div className="pc-storage-card-meta">
              <span>
                {loading ? (
                  '—'
                ) : (
                  <>
                    <span className="pc-storage-card-pct">{formatPctLabel(percent)}</span> utilizado
                  </>
                )}
              </span>
              <span>{loading ? '—' : `${formatGb(free, 2)} GB livres`}</span>
            </div>
          </div>

          {error ? (
            <p className="pc-storage-error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            className="pc-storage-card-mng"
            onClick={() => setBrowserOpen(true)}
          >
            <FolderOpen size={17} strokeWidth={1.8} aria-hidden />
            <span className="pc-storage-card-mng-label">Gerenciar arquivos</span>
            <ChevronRight size={16} strokeWidth={2} className="pc-storage-card-chev" aria-hidden />
          </button>
        </div>
      </Pcs2Section>

      <CompanyStorageBrowser open={browserOpen} onClose={() => setBrowserOpen(false)} />
    </div>
  );
}
